/**
 * FunnelDeleteModal
 *
 * Modal de exclusão de um funil que AINDA TEM LEADS (RD2 da Fase C2).
 *
 * Antes desta tela a única saída era o 400 do backend ("Cannot delete funnel
 * with N linked lead(s)") — o usuário via o erro e não tinha o que fazer.
 * Agora ele decide o destino dos leads:
 *
 * - **Excluir todos os N leads** → `DELETE /funnels/:id` com `{ leads: { action: 'delete' } }`
 * - **Transferir para outro funil** → `{ leads: { action: 'transfer', funnelId, stepId } }`
 *
 * Regras da tela:
 * - campos EMPILHADOS (um abaixo do outro), componentes shared, tema claro fixo
 *   (o `Modal` já força as CSS variables claras);
 * - funil de destino nunca é o próprio funil sendo excluído (o backend também
 *   recusa com 422, mas a lista já não oferece a opção);
 * - a etapa só abre depois do funil escolhido (a etapa precisa pertencer a ele);
 * - "Excluir funil" só habilita com uma escolha completa.
 *
 * Funil SEM leads não passa por aqui — segue na confirmação simples do
 * `ConfirmProvider`, como sempre foi.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalContent, ModalFooter, Button, Select, RadioGroup } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { FunnelLeadsDisposition, FunnelResponse } from '@troia-v3/types';
import { useFunnelsDropdown, useFunnelStepsDropdown } from '../hooks/useFunnels';

export interface FunnelDeleteModalProps {
  /** Modal aberto? */
  isOpen: boolean;
  /** Funil que será excluído (usado para nome e para excluir-se da lista de destino) */
  funnel: FunnelResponse | null;
  /** Quantidade de leads vinculados, vinda de `GET /funnels/validate-delete/:id` */
  leadCount: number;
  /** Fecha sem excluir */
  onClose: () => void;
  /** Confirma a exclusão com o destino escolhido para os leads */
  onConfirm: (leads: FunnelLeadsDisposition) => void;
  /** Exclusão em andamento (trava os botões) */
  isDeleting?: boolean;
}

type DeleteChoice = 'delete' | 'transfer';

export const FunnelDeleteModal: React.FC<FunnelDeleteModalProps> = ({
  isOpen,
  funnel,
  leadCount,
  onClose,
  onConfirm,
  isDeleting = false,
}) => {
  const [choice, setChoice] = useState<DeleteChoice | ''>('');
  const [targetFunnelId, setTargetFunnelId] = useState('');
  const [targetStepId, setTargetStepId] = useState('');

  // Lazy fetching (CLAUDE.md NUNCA #14): as duas listas só saem do lugar com o
  // modal aberto — e a de etapas só depois do funil de destino escolhido.
  const { data: funnels } = useFunnelsDropdown({ enabled: isOpen });
  const { data: steps } = useFunnelStepsDropdown(isOpen && targetFunnelId ? targetFunnelId : undefined);

  // Estado zerado a cada abertura — abrir o modal de outro funil não pode
  // herdar o destino escolhido na vez anterior.
  useEffect(() => {
    if (!isOpen) return;
    setChoice('');
    setTargetFunnelId('');
    setTargetStepId('');
  }, [isOpen, funnel?.id]);

  const funnelOptions = useMemo(
    () =>
      (funnels ?? [])
        .filter((f) => f.id !== funnel?.id)
        .map((f) => ({ value: f.id, label: f.name })),
    [funnels, funnel?.id],
  );

  const stepOptions = useMemo(
    () => (steps ?? []).map((s) => ({ value: s.id, label: s.name })),
    [steps],
  );

  const canConfirm =
    choice === 'delete' || (choice === 'transfer' && !!targetFunnelId && !!targetStepId);

  const handleConfirm = () => {
    if (choice === 'delete') {
      onConfirm({ action: 'delete' });
      return;
    }
    if (choice === 'transfer' && targetFunnelId && targetStepId) {
      onConfirm({ action: 'transfer', funnelId: targetFunnelId, stepId: targetStepId });
    }
  };

  if (!isOpen || !funnel) return null;

  const leadLabel = leadCount === 1 ? '1 lead' : `${leadCount} leads`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="lg">
      <div data-testid="funnel-delete-modal">
        <ModalHeader title="Excluir funil" onClose={onClose} />
        <ModalContent>
          <div className="space-y-4">
            <div
              className="flex items-start gap-3 p-3 rounded-lg"
              style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}
            >
              <Icon name="alert-triangle" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#d97706' }} />
              <div className="text-sm" style={{ color: '#78350f' }}>
                O funil <strong>{funnel.name}</strong> tem {leadLabel}. Escolha o que fazer com
                {leadCount === 1 ? ' ele' : ' eles'} antes de excluir — esta ação não pode ser desfeita.
              </div>
            </div>

            <RadioGroup
              label="O que fazer com os leads"
              value={choice}
              onChange={(v) => {
                setChoice(v as DeleteChoice);
                if (v === 'delete') {
                  setTargetFunnelId('');
                  setTargetStepId('');
                }
              }}
              options={[
                {
                  value: 'delete',
                  label: `Excluir ${leadCount === 1 ? 'o lead' : `todos os ${leadCount} leads`}`,
                  description: 'Os leads e o histórico deles saem do CRM junto com o funil.',
                },
                {
                  value: 'transfer',
                  label: 'Transferir para outro funil',
                  description: 'Os leads continuam no CRM, na etapa que você escolher abaixo.',
                },
              ]}
              variant="cards"
              data-testid="funnel-delete-option"
            />

            {choice === 'transfer' && (
              <>
                <div>
                  <Select
                    label="Funil de destino"
                    value={targetFunnelId}
                    onChange={(v) => {
                      setTargetFunnelId(v);
                      // A etapa pertence ao funil — trocar o funil invalida a escolha
                      // anterior (o backend recusa com 422 etapa de outro funil).
                      setTargetStepId('');
                    }}
                    options={funnelOptions}
                    placeholder="Selecione o funil..."
                    fullWidth
                    data-testid="funnel-delete-target-funnel"
                  />
                </div>
                <div>
                  <Select
                    label="Etapa de destino"
                    value={targetStepId}
                    onChange={setTargetStepId}
                    options={stepOptions}
                    placeholder={targetFunnelId ? 'Selecione a etapa...' : 'Escolha o funil primeiro'}
                    disabled={!targetFunnelId}
                    fullWidth
                    data-testid="funnel-delete-target-step"
                  />
                </div>
              </>
            )}
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} disabled={isDeleting} data-testid="funnel-delete-cancel">
            Cancelar
          </Button>
          <Button
            variant="error"
            onClick={handleConfirm}
            disabled={!canConfirm || isDeleting}
            loading={isDeleting}
            data-testid="funnel-delete-confirm"
          >
            Excluir funil
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

FunnelDeleteModal.displayName = 'FunnelDeleteModal';

export default FunnelDeleteModal;
